import React, { useCallback, useMemo } from 'react'
import { Layout, Avatar, Dropdown, Tooltip, theme } from 'antd'
import {
  UserOutlined,
  SettingOutlined,
  LogoutOutlined,
  DashboardOutlined,
  CreditCardOutlined,
  SafetyCertificateOutlined,
} from '@ant-design/icons'
import { Link, Outlet, useNavigate } from 'react-router-dom'
import styled from 'styled-components'
import { useAuth } from '@/libs/context/auth'
import LogoTuba from '@/assets/logoTuba'
import LanguageSwitcher from '@/components/LanguageSwitcher.jsx'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { getStaleTime, QUERY_TYPES } from '@/config/reactQuery'
import SettingsService from '@/libs/services/settings'
import { useSubscriptions } from '@/features/account/hooks/useSubscriptions'
import { useActiveTier } from '@/features/account/hooks/useActiveTier'
import { spacing } from '@/libs/theme'
import moment from 'moment'

const { Header, Content } = Layout

const Logo = styled.div`
  height: 32px;
  margin: 16px;
  display: flex;
  align-items: center;
  color: white;
  font-size: 18px;
  font-weight: bold;
  cursor: pointer;

  img {
    width: 32px;
    height: 32px;
    margin-right: 8px;
  }
  gap: 16px;

  @media (max-width: 768px) {
    margin: 8px;
    gap: 8px;
  }

  @media (max-width: 480px) {
    margin: 4px;
    gap: 4px;
  }
`

const LogoText = styled.span`
  font-size: 20px;
  font-weight: bold;
  color: rgba(0, 0, 0, 0.85);

  @media (max-width: 768px) {
    font-size: 18px;
  }

  @media (max-width: 480px) {
    font-size: 16px;
  }
`

const StyledHeader = styled(Header)`
  padding: 0 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: white;
  position: sticky;
  top: 0;
  z-index: 1000;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    padding: 0 16px;
    position: relative;
  }

  @media (max-width: 480px) {
    padding: 0 8px;
  }
`

const UserSection = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  @media (max-width: 768px) {
    gap: 12px;
  }

  @media (max-width: 480px) {
    gap: 8px;
  }
`

const UserNameBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  margin-right: 8px;
  line-height: 1.25;

  @media (max-width: 768px) {
    margin-right: 4px;
    font-size: 14px;
  }

  @media (max-width: 480px) {
    display: none;
  }
`

const UserName = styled.span``

const UserOrgSub = styled.span`
  font-size: 0.85em;
  opacity: 0.9;
`

const UserAvatar = styled.div`
  display: flex;
  align-items: center;
  cursor: pointer;
  gap: 8px;

  @media (max-width: 768px) {
    gap: 4px;
  }

  @media (max-width: 480px) {
    gap: 0;
  }
`

const MainContent = styled(Content)`
  padding: 24px;
  min-height: 280px;
  overflow: auto;

  @media (max-width: 768px) {
    padding: 16px;
  }

  @media (max-width: 480px) {
    padding: 12px;
  }
`

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  width: 100%;

  @media (max-width: 768px) {
    justify-content: space-between;
  }
`

const HeaderSection = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: ${({ $align }) => $align || 'flex-start'};
  min-width: 0;

  @media (max-width: 768px) {
    flex: none;
  }
`

const CenterSection = styled.div`
  flex: 0 1 auto;
  display: flex;
  justify-content: center;
  align-items: center;
  min-width: 0;

  @media (max-width: 768px) {
    display: none;
  }
`

const TrialInfoMobile = styled.div`
  display: none;
  text-align: center;
  line-height: 1;
  font-size: 12px;

  @media (max-width: 768px) {
    display: block;
    position: absolute;
    top: 80%;
    left: 0;
    right: 0;
    padding: 8px;
    z-index: 0;
  }
`

const NoMenuLayout = () => {
  const { token } = theme.useToken()
  const { logout } = useAuth()
  //const location = useLocation()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const { data: me } = useQuery({
    queryKey: ['me'],
    queryFn: () => SettingsService.getMe(),
    staleTime: getStaleTime(QUERY_TYPES.USER),
  })

  const { data: organizationData } = useQuery({
    queryKey: ['organization'],
    queryFn: () => SettingsService.getOrganization(),
    staleTime: getStaleTime(QUERY_TYPES.ORGANIZATION),
  })

  const organization = organizationData?.data
  const handleLogout = useCallback(() => {
    logout()
    navigate('/auth/login')
  }, [logout, navigate])

  const { currentSubscription, organizationSubscriptions } = useSubscriptions()
  const { activeTier, isDemoMode } = useActiveTier()
  const isCustomTier = activeTier && (activeTier.id === 'CUSTOM' || activeTier.isCustom)

  const hasSubscription = useMemo(() => {
    const subs = organizationSubscriptions || []
    if (subs.length === 0) return false
    return !!(activeTier || isDemoMode)
  }, [organizationSubscriptions, activeTier, isDemoMode])

  const userMenuItems = [
    {
      key: 'products',
      label: hasSubscription ? (
        <Link to="/dashboard">{t('layout.productsPanel')}</Link>
      ) : (
        <Tooltip title={t('layout.selectSubscriptionToAccess')} placement="left">
          <span style={{ color: token.colorTextDisabled }}>{t('layout.productsPanel')}</span>
        </Tooltip>
      ),
      icon: <DashboardOutlined />,
      disabled: !hasSubscription,
    },
    {
      type: 'divider',
    },
    {
      key: 'organization',
      label: <Link to="/organization">{t('layout.organization')}</Link>,
      icon: <SettingOutlined />,
    },
    {
      key: 'user',
      label: <Link to="/user">{t('layout.user')}</Link>,
      icon: <UserOutlined />,
    },
    {
      key: 'subscriptions',
      label: <Link to="/subscriptions">{t('layout.subscriptions')}</Link>,
      icon: <CreditCardOutlined />,
    },
    {
      key: 'permissions',
      label: hasSubscription ? (
        <Link to="/permissions">{t('layout.permissions')}</Link>
      ) : (
        <Tooltip title={t('layout.selectSubscriptionToAccess')} placement="left">
          <span style={{ color: token.colorTextDisabled }}>{t('layout.permissions')}</span>
        </Tooltip>
      ),
      icon: <SafetyCertificateOutlined />,
      disabled: !hasSubscription,
    },
    ...(isCustomTier
      ? [
          { type: 'divider' },
          {
            key: 'tier',
            label: (
              <span style={{ cursor: 'default', color: token.colorTextTertiary }}>
                {t('layout.yourTier')}: {t('subscriptions.customPlan')}
              </span>
            ),
            disabled: true,
          },
        ]
      : []),
    {
      type: 'divider',
    },
    {
      key: 'logout',
      label: t('layout.logout'),
      icon: <LogoutOutlined />,
      onClick: handleLogout,
    },
  ]

  const futureDate = moment(currentSubscription?.finishDate)

  const currentDate = moment()

  const daysLeft = futureDate.diff(currentDate, 'days')

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Layout>
        <StyledHeader>
          <HeaderContent>
            <HeaderSection $align="flex-start">
              <div id="logo">
                <Logo>
                  <div
                    onClick={() => navigate('/')}
                    style={{ display: 'flex', alignItems: 'center', gap: spacing.sm }}
                  >
                    <LogoTuba width="3rem" height="3rem" />
                    <LogoText>Tuba</LogoText>
                  </div>
                </Logo>
              </div>
            </HeaderSection>
            <CenterSection>
              <div id="trial-info" style={{ textAlign: 'center', lineHeight: '1' }}>
                {currentSubscription?.isTrial && (
                  <span>{t('subscriptions.trialEndsIn', { days: daysLeft })}</span>
                )}
              </div>
            </CenterSection>
            <HeaderSection $align="flex-end">
              <UserSection id="user-section">
                <LanguageSwitcher />
                <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
                  <UserAvatar>
                    <UserNameBlock>
                      <UserName>
                        {[
                          me?.data?.firstName ?? me?.data?.first_name,
                          me?.data?.lastName ?? me?.data?.last_name,
                        ]
                          .filter(Boolean)
                          .join(' ') || 'User'}
                      </UserName>
                      {(organization?.nickname?.trim() || organization?.name?.trim()) && (
                        <UserOrgSub>
                          ({organization?.nickname?.trim() || organization?.name?.trim()})
                        </UserOrgSub>
                      )}
                    </UserNameBlock>

                    <Avatar
                      style={{ backgroundColor: token.colorPrimary, cursor: 'pointer' }}
                      icon={<UserOutlined />}
                    />
                  </UserAvatar>
                </Dropdown>
              </UserSection>
            </HeaderSection>
          </HeaderContent>
          <TrialInfoMobile
            style={{
              color: token.colorTextSecondary,
              background: token.colorBgContainer,
            }}
          >
            {currentSubscription?.isTrial && (
              <span>{t('subscriptions.trialEndsIn', { days: daysLeft })}</span>
            )}
          </TrialInfoMobile>
        </StyledHeader>

        <MainContent>
          <Outlet />
        </MainContent>
      </Layout>
    </Layout>
  )
}

export default NoMenuLayout
